import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardContent,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { LoadingSkeletonRows } from '../shared/LoadingSkeletonRows'
import ErrorMessage from '../../component/shared/component/ErrorMessage'
import Upload from './Upload'
import { MANUAL_SOURCE, getTicketFieldOptions } from '../../utils/luckyDraw'
import { useGetLuckyDrawEntries, useGetLuckyDrawSources } from '../../api/lucky-draw'

// One shared empty array: a fresh [] per render would change the filter memo's dependency every
// time and stop it holding.
const NO_COMPANIES = []

const Entries = ({ id, source, hasWinners }) => {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const { data, isLoading, error, refetch } = useGetLuckyDrawEntries(id)
  const { data: sources = [] } = useGetLuckyDrawSources()

  const companies = data?.companies ?? NO_COMPANIES
  // Every ticket type the source offers, whether or not a group happens to draw on it.
  const ticketFields = getTicketFieldOptions(sources, source)
  const isManual = source === MANUAL_SOURCE

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase()

    if (!needle) return companies

    return companies.filter((company) => company.companyName.toLowerCase().includes(needle))
  }, [companies, search])

  const visible = matches.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
  const columnCount = ticketFields.length + 2

  const handleChangePage = (event, newPage) => setPage(newPage)

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  useEffect(() => setPage(0), [search])

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant='h2' sx={{ fontSize: '1.5rem' }} mt={3} mb={3}>
          Entries
        </Typography>

        {/* Manual draws only: a GMN/CNN pool comes from AX and has no sheet to replace. */}
        {isManual && <Upload id={id} hasWinners={hasWinners} onUploaded={refetch} />}

        <TextField
          size='small'
          label='Search company'
          variant='outlined'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, mb: 3 }}
        />

        {error ? (
          <ErrorMessage error={error} onRetry={refetch} />
        ) : matches.length > 0 || isLoading ? (
          <>
            <TableContainer component={Paper}>
              <MuiTable>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    {ticketFields.map((option) => (
                      <TableCell key={option.key}>{option.label}</TableCell>
                    ))}
                    <TableCell>Prizes Won</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <LoadingSkeletonRows rows={10} length={columnCount} />
                  ) : (
                    visible.map((company) => (
                      <TableRow key={company.companyKey}>
                        <TableCell>{company.companyName}</TableCell>
                        {ticketFields.map((option) => (
                          <TableCell key={option.key}>{company.tickets[option.key]}</TableCell>
                        ))}
                        <TableCell>{company.wins}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </MuiTable>
            </TableContainer>
            {!isLoading && (
              <TablePagination
                component='div'
                count={matches.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </>
        ) : (
          <ErrorMessage
            empty
            message={
              companies.length > 0
                ? 'No company matches that search.'
                : isManual
                  ? 'No entry list uploaded yet.'
                  : 'No companies found for this source.'
            }
          />
        )}
      </CardContent>
    </Card>
  )
}

export default Entries
